import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild, WritableSignal, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { AssignedPartnershipInfo, Customer, CustomerService, TripDetail, TripDetailService } from 'src/app/api';
import { JournalHomeTrip, Journals, TripJournalService } from 'src/app/api/trip-journal.service';
import { ErrorService } from 'src/app/services/error.service';
import { GroupPhotoComponent } from '../group-photo/group-photo.component';
import { TripPartnershipService } from 'src/app/api/trip-partnership.service';
import { ButtonModule } from 'primeng/button';
import { IconService } from 'src/app/ui/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ConfigService } from 'src/app/config.service';
import { FileProgressEvent, FileSendEvent, FileUpload, FileUploadErrorEvent, FileUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { ProseMirrorComponent } from 'src/app/ui/prose-mirror/prose-mirror.component';
import { Dialog, DialogModule } from 'primeng/dialog';
import { TripJournalEditComponent } from "../trip-journal-edit/trip-journal-edit.component";
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SanitizeHtmlPipe } from 'src/app/ui/pipes/safe-html.pipe';
import { IncidentModalService } from '../../incidents/modals/incident-modal.service';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageBoxResults } from '../../incidents/modals/message-box/message-box.component';
import { ContactCardUrlPipe } from 'src/app/ui/pipes/contact-card-url.pipe';
import { TripJournalLinkPipe } from 'src/app/ui/trip-journal-link.pipe';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-trip-journal',
  standalone: true,
  imports: [SanitizeHtmlPipe, CardModule, DatePipe, GroupPhotoComponent, ButtonModule, FontAwesomeModule, FileUploadModule, DialogModule, TripJournalEditComponent, ScrollPanelModule, ContactCardUrlPipe, TripJournalLinkPipe],
  providers: [DialogService],
  templateUrl: './trip-journal.component.html',
  styleUrl: './trip-journal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripJournalComponent implements OnInit {




  trip: WritableSignal<TripDetail | undefined> = signal(undefined)
  customer: WritableSignal<Customer> = signal(this.customerService.emptyCustomer())
  partnerInfo: WritableSignal<AssignedPartnershipInfo> = signal(this.partnerService.emptyPartnershipInfo())
  journalDialogVisible: WritableSignal<boolean> = signal(false)

  currentUser = toSignal(this.auth.currentUser$)


  entries: WritableSignal<Journals[]> = signal([])

  editEntry = signal<Journals | undefined>(undefined)
  
  photoCacheBust: WritableSignal<Map<number, number>> = signal(new Map<number, number>())


  communityList = computed(() => {
    return this.partnerInfo().partnership.communities.map(c => c.name).join(", ")
  })

  partnerList = computed(() => {
    return this.partnerInfo().partnership.partners.map(p => p.name).join(", ")
  })

  routeTripId: number = -1

  @ViewChild('journalDialog') journalDialog!: Dialog
  @ViewChild('journalEdit') journalEdit!: TripJournalEditComponent

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private journalService: TripJournalService,
    private messageService: MessageService,
    private errorService: ErrorService,
    private tripDetailService: TripDetailService,
    private customerService: CustomerService,
    private partnerService: TripPartnershipService,
    public icons: IconService,
    public config: ConfigService,
    public modalService: IncidentModalService,
    private dialogService: DialogService,
    private auth: LocalAuthService

  ) {

  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(value => {
      const tripId = Number(value.get('tripId'))
      this.routeTripId = tripId

      if (!isNaN(tripId)) {

        this.loadJournal(tripId)
      }

    })

  }

  loadJournal(tripId: number) {

    this.journalService.getIndividualJournal(tripId).subscribe({
      next: result => {
        this.trip.set(result.trip)
        this.customer.set(result.customer)
        this.partnerInfo.set(result.partnership)
        this.entries.set(result.entries)
        this.photoCacheBust.set(new Map<number, number>(result.entries.map(e => [e.id, 1])))
      },
      error: err => {
        this.errorService.error("There was a problem loading the trip journal", err, this.messageService, `route trip id: ${this.routeTripId}`)
      }
    })

  
  }

  groupPhotoUploaded() {
    this.loadJournal(this.trip()?.id ?? 0)
  }

  addJournalEntry() {
    this.editEntry.set(undefined)
    this.journalEdit.clear()
    this.journalDialogVisible.set(true)
  }

  entryCreated(entry: Journals) {
    this.entries.update(value => [...value, entry])
    this.journalDialogVisible.set(false)
    this.photoCacheBust.update(value => value.set(entry.id, 1))
    // Notify about the new journal entry
    this.journalService.notifyJournalEntry(entry.id).subscribe({
      error: err => {
        this.errorService.error("Failed to send notifications for journal entry", err, this.messageService)
      }
    })
  }

  entryEdited(entry: Journals) {
    this.entries.update(value => value.map(e => e.id === entry.id ? entry : e))
    this.journalDialogVisible.set(false)
    this.photoCacheBust.update(value => value.set(entry.id, (value.get(entry.id) ?? 0) + 1))
  }

  cancelEdit() {
    this.journalDialogVisible.set(false)
  }

  editEntryClicked(entry: Journals) {
    this.journalEdit.clear()
    this.editEntry.set({...entry})
    this.journalDialogVisible.set(true)
  }

  deleteEntryClicked(entry: Journals) {

    this.modalService.showMessageBox(this.dialogService, "Delete Entry", "Are you sure you want to delete this entry?", { confirmButtonText: "Yes", cancelButtonText: "Cancel", position: "center" }).onClose.subscribe(result => {
      if (result === MessageBoxResults.Confirmed) {
        this.journalService.deleteJournalEntry(entry.id).subscribe({
          next: () => {
            this.entries.update(value => value.filter(e => e.id !== entry.id))
          }
        })
      }
    })


  }

}
